package horcrux.frontend.common.views.collections.importdocuments

import arrow.core.Either
import arrow.core.NonEmptyList
import arrow.core.none
import arrow.core.raise.either
import arrow.core.right
import arrow.core.some
import arrow.fx.coroutines.parMap
import horcrux.common.DuplicateValueError
import horcrux.common.HorcruxEither
import horcrux.common.api.dtos.RawDocumentClientSideID
import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.CollectionName
import horcrux.common.api.dtos.types.DocumentIdentifier
import horcrux.common.api.dtos.types.DocumentInput
import horcrux.common.api.dtos.types.DocumentName
import horcrux.common.api.dtos.types.DocumentURL
import horcrux.common.api.dtos.types.InvalidCollectionName
import horcrux.common.api.dtos.types.InvalidDocumentName
import horcrux.common.api.dtos.types.RawDocumentName
import horcrux.common.api.dtos.types.UploadedDocumentIdentifier
import horcrux.common.extensions.parMap
import horcrux.common.extensions.toNonEmptyList
import horcrux.common.types.NonEmptyMap
import horcrux.common.utils.EnumCompanion
import horcrux.common.utils.StringEnum
import horcrux.common.utils.Validatable
import horcrux.common.utils.eitherWay
import horcrux.common.utils.raiseError
import horcrux.common.utils.toOptional
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.DocumentByteArray
import horcrux.frontend.common.DocumentFile
import horcrux.frontend.common.DocumentInputData
import horcrux.frontend.common.DocumentText
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.LocalDocumentInput
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.ViewRefreshState
import horcrux.frontend.common.clients.backendclient.AuthenticatedBackendClient
import horcrux.frontend.common.handleErrors
import horcrux.frontend.common.toByteArray
import horcrux.frontend.common.views.chat.InputPrompt
import horcrux.frontend.common.views.chat.PromptSubmitter
import horcrux.frontend.common.views.collections.collectiondialogs.CollectionMediaType
import horcrux.frontend.common.views.collections.collectiondialogs.createCollectionAndHandleResponse
import io.ktor.utils.io.core.toByteArray
import js.promise.Promise
import mui.material.AlertColor
import web.file.File

fun GlobalAppState.createCollectionAndImportDocuments(
    collectionName: CollectionName,
    collectionMediaType: CollectionMediaType,
    documentInputDataByClientSideID: NonEmptyMap<RawDocumentClientSideID, DocumentInputData>,
    inputPromptMaybeState: MaybeState<InputPrompt>,
    isLoadingState: BooleanState,
    viewRefreshState: ViewRefreshState,
    closeDialog: () -> Unit,
    updateCollectionMetadata: (CollectionMetadata) -> Unit,
) = this.submitBackendAPIRequestAsync { backendClient ->
    isLoadingState.setTrue()
    val postCollectionResponse = backendClient.createCollectionAndHandleResponse(
        collectionName, collectionMediaType.embeddingModelID, this@createCollectionAndImportDocuments.updateBanner
    ).bind()
    backendClient.importDocuments(
        postCollectionResponse.collectionMetadata,
        documentInputDataByClientSideID,
        inputPromptMaybeState,
        this@createCollectionAndImportDocuments,
        isLoadingState,
        viewRefreshState,
        closeDialog,
        updateCollectionMetadata,
    )
        .onLeft { backendClient.deleteCollection(postCollectionResponse.collectionMetadata.id).bind() }
        .eitherWay { isLoadingState.setFalse() }
        .bind()
}

fun GlobalAppState.importDocuments(
    collectionMetadata: CollectionMetadata,
    documentInputDataByClientSideID: NonEmptyMap<RawDocumentClientSideID, DocumentInputData>,
    inputPromptMaybeState: MaybeState<InputPrompt>,
    globalAppState: GlobalAppState,
    isLoadingState: BooleanState,
    viewRefreshState: ViewRefreshState,
    closeDialog: () -> Unit,
    updateCollectionMetadata: (CollectionMetadata) -> Unit,
) = this.submitBackendAPIRequestAsync { backendClient ->
    isLoadingState.setTrue()
    backendClient.importDocuments(
        collectionMetadata,
        documentInputDataByClientSideID,
        inputPromptMaybeState,
        globalAppState,
        viewRefreshState,
        isLoadingState,
        closeDialog,
        updateCollectionMetadata,
    ).bind()
}

private suspend fun AuthenticatedBackendClient.importDocuments(
    collectionMetadata: CollectionMetadata,
    documentInputDataByClientSideID: NonEmptyMap<RawDocumentClientSideID, DocumentInputData>,
    inputPromptMaybeState: MaybeState<InputPrompt>,
    globalAppState: GlobalAppState,
    viewRefreshState: ViewRefreshState,
    isLoadingState: BooleanState,
    closeDialog: () -> Unit,
    updateCollectionMetadata: (CollectionMetadata) -> Unit,
): Either<Throwable, NonEmptyList<UploadedDocumentIdentifier>> = either {
    val uploadedDocumentIdentifiers = documentInputDataByClientSideID
        .filter { it.value.documentInputMaybe.valueMaybe != null }
        .parMap { rawClientSideID, documentInputData ->
            val result = documentInputData
                .documentIdentifierMaybe
                ?.let { it.right() }
                ?: run {
                    globalAppState
                        .uploadRawDocument(documentInputData, isLoadingState)
                        .map {
                            val rawDocumentID = it.rawDocumentMetadataClientSideID[rawClientSideID]?.rawDocumentID
                                ?: raiseError("$rawClientSideID is not in response")
                            UploadedDocumentIdentifier(rawDocumentID, documentInputData.documentName)
                        }
                }
            rawClientSideID to result
        }
        .map { it.value.bind() }

    uploadedDocumentIdentifiers
        .toNonEmptyList()
        .onRight { documentIdentifiers ->
            documentIdentifiers.chunked(1).parMap { documentIdentifierBatch ->
                this@importDocuments.importDocumentAndHandleResponse(
                    documentIdentifierBatch.toNonEmptyList().bind(),
                    collectionMetadata,
                    globalAppState,
                    viewRefreshState,
                ).bind()
            }
            // Update the CollectionMetadata here instead of in createCollectionAndImportDocuments
            // Otherwise the dialog will display an invalid CollectionName error while running importDocumentAndHandleResponse
            updateCollectionMetadata(collectionMetadata)
        }
        .onRight {
            inputPromptMaybeState.get()?.let {
                globalAppState.shouldShowCollectionNavigatorState.setFalse()
                PromptSubmitter(globalAppState).onSubmitPromptAction(inputPromptMaybeState, collectionMetadata)
            }
        }
        .onLeft { globalAppState.updateBanner(AlertColor.error, "You must import at least one document") }
        .bind()
}
    .handleErrors { cause ->
        when (cause) {
            is InvalidCollectionName, is DuplicateValueError -> {
                globalAppState.updateBanner(
                    AlertColor.error, "Collection name is invalid or not unique. Try inputting a new collection name."
                )
                none()
            }

            is InvalidDocumentName -> {
                globalAppState.updateBanner(
                    AlertColor.error, "Document name is invalid or not unique. Try inputting a new document name."
                )
                none()
            }

            else -> cause.some()
        }
    }.eitherWay { isLoadingState.setFalse() }
    .onRight { closeDialog() }

enum class DocumentInputType(override val stringValue: String) : StringEnum {
    URL("Url"), FILE("File"), TEXT("Text");

    fun getDocumentInputMaybe(): DocumentInputMaybe<out DocumentInput> = when (this) {
        URL -> DocumentURLMaybe()
        FILE -> DocumentFileMaybe()
        TEXT -> DocumentTextMaybe()
    }

    companion object : EnumCompanion<DocumentInputType> {
        override val valuesList: List<DocumentInputType> = DocumentInputType.values().toList()
    }

}

sealed interface DocumentInputMaybe<A : DocumentInput> : Validatable<DocumentInputMaybe<A>> {
    val documentInputType: DocumentInputType
    val valueMaybe: A?
    val defaultDocumentName: DocumentName
        get() = valueMaybe?.defaultDocumentName ?: DocumentName("")

    suspend fun filterIfNotValidAndMap(
        inputIsInvalid: () -> Boolean, globalAppState: GlobalAppState, block: suspend (A) -> DocumentIdentifier
    ): DocumentIdentifier? = valueMaybe?.let { value ->
        inputIsInvalid().not().toOptional()?.let {
            block(value)
        }
    } ?: run {
        globalAppState.updateBanner(
            AlertColor.error, "Please fill all required fields with valid inputs"
        )
        null
    }
}

sealed interface LocalDocumentInputMaybe<A : LocalDocumentInput> : DocumentInputMaybe<A> {
    val byteArrayMaybe: Promise<ByteArray>?
}

data class DocumentURLMaybe(override val valueMaybe: DocumentURL?) : DocumentInputMaybe<DocumentURL> {
    constructor() : this(null)
    constructor(value: String) : this(DocumentURL(value))

    override val documentInputType: DocumentInputType = DocumentInputType.URL

    override fun validate(): HorcruxEither<DocumentURLMaybe> =
        this.valueMaybe?.validate()?.map { DocumentURLMaybe(it) } ?: this.right()
}

data class DocumentByteArrayMaybe(override val valueMaybe: DocumentByteArray?) :
    LocalDocumentInputMaybe<DocumentByteArray> {
    constructor(byteArrayMaybe: ByteArray?, rawDocumentName: RawDocumentName) : this(
        byteArrayMaybe?.let { DocumentByteArray(it, rawDocumentName) })

    override val documentInputType: DocumentInputType = DocumentInputType.FILE
    override val byteArrayMaybe: Promise<ByteArray>?
        get() = valueMaybe?.value?.let { Promise.resolve(it) }

    override fun validate(): HorcruxEither<DocumentByteArrayMaybe> = Either.Right(this)

    companion object
}

data class DocumentFileMaybe(override val valueMaybe: DocumentFile?) : LocalDocumentInputMaybe<DocumentFile> {
    constructor() : this(valueMaybe = null)
    constructor(fileMaybe: File?) : this(fileMaybe?.let { DocumentFile(it) })

    override val documentInputType: DocumentInputType = DocumentInputType.FILE
    override val byteArrayMaybe: Promise<ByteArray>?
        get() = valueMaybe?.value?.arrayBuffer()?.then { it.toByteArray() }

    override fun validate(): HorcruxEither<DocumentFileMaybe> = Either.Right(this)

    companion object
}

data class DocumentTextMaybe(override val valueMaybe: DocumentText?) : LocalDocumentInputMaybe<DocumentText> {
    constructor() : this(null)
    constructor(value: String) : this(DocumentText(value))

    override val documentInputType: DocumentInputType = DocumentInputType.TEXT
    override val byteArrayMaybe: Promise<ByteArray>?
        get() = valueMaybe?.let { Promise.resolve(it.value.toByteArray()) }

    override fun validate(): HorcruxEither<DocumentTextMaybe> = Either.Right(this)

    companion object
}
